import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RiInformationLine } from 'react-icons/ri';
import { STACK_ICONS, STACK_TOOLTIPS } from '../../constants';
import { WorkflowIcon } from './WorkflowIconPicker';

// Same wrench SVG used in the app Header for the Workflow Builder nav item.
const WrenchIcon = () => (
  <svg
    width="14" height="14" viewBox="0 0 24 24"
    fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

// ---------------------------------------------------------------------------
// Node-type colours (mirror WorkflowBuilder constants)
const NODE_COLORS = {
  input: '#22c55e', agent: '#3b82f6',
  tool: '#f97316', logic: '#a855f7', output: '#ef4444',
};

// Topological sort of dag_json nodes — returns nodes in execution order.
function buildNodeChain(dagJson) {
  const nodes = dagJson?.nodes || [];
  const edges = dagJson?.edges || [];
  if (nodes.length === 0) return [];
  const adj = {};
  const inDeg = {};
  nodes.forEach((n) => { adj[n.id] = []; inDeg[n.id] = 0; });
  edges.forEach((e) => {
    adj[e.source]?.push(e.target);
    if (inDeg[e.target] !== undefined) inDeg[e.target]++;
  });
  const queue = nodes.filter((n) => inDeg[n.id] === 0).map((n) => n.id);
  const result = [];
  const seen = new Set();
  while (queue.length > 0) {
    const id = queue.shift();
    if (seen.has(id)) continue;
    seen.add(id);
    const node = nodes.find((n) => n.id === id);
    if (node) result.push(node);
    (adj[id] || []).forEach((tid) => {
      inDeg[tid]--;
      if (inDeg[tid] === 0) queue.push(tid);
    });
  }
  return result;
}

// Single workflow option with description subtitle + expandable info panel.
function WorkflowDropdownItem({ wf, isActive, onSelect }) {
  const [infoOpen, setInfoOpen] = useState(false);
  const chain = React.useMemo(() => buildNodeChain(wf.dag_json), [wf.dag_json]);

  const color = isActive ? 'var(--primary-color, #636cff)' : 'var(--text-color)';
  const iconColor = isActive ? 'var(--primary-color, #636cff)' : 'var(--text-secondary)';

  return (
    <div>
      {/* Main row: icon + name/description + info toggle */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <button
          type="button"
          onClick={() => onSelect(wf.id)}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 6px 6px 12px',
            background: 'transparent', border: 'none',
            cursor: 'pointer', textAlign: 'left', minWidth: 0,
          }}
        >
          <WorkflowIcon iconKey={wf.icon} size={14} color={iconColor} />
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
            <span style={{
              fontSize: '0.82rem', fontWeight: isActive ? 600 : 400,
              color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {wf.name}
            </span>
            {wf.description && (
              <span style={{
                fontSize: '0.67rem', color: 'var(--text-secondary)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                marginTop: 1,
              }}>
                {wf.description.length > 44
                  ? wf.description.slice(0, 44) + '…'
                  : wf.description}
              </span>
            )}
          </span>
        </button>
        {/* Info toggle — does not select the workflow */}
        <button
          type="button"
          title={infoOpen ? 'Hide details' : 'About this workflow'}
          onClick={(e) => { e.stopPropagation(); setInfoOpen((o) => !o); }}
          style={{
            flexShrink: 0, background: 'none', border: 'none',
            cursor: 'pointer', padding: '4px 10px 4px 4px',
            display: 'flex', alignItems: 'center',
            color: infoOpen ? 'var(--primary-color, #636cff)' : 'var(--text-secondary)',
            opacity: infoOpen ? 1 : 0.65,
            transition: 'color 0.15s, opacity 0.15s',
          }}
        >
          <RiInformationLine size={15} />
        </button>
      </div>

      {/* Expandable info panel */}
      {infoOpen && (
        <div style={{
          padding: '6px 12px 10px',
          borderTop: '1px solid var(--border-color, #eee)',
          borderBottom: '1px solid var(--border-color, #eee)',
          background: 'var(--hover-color, rgba(0,0,0,0.03))',
        }}>
          {wf.description && (
            <p style={{
              fontSize: '0.74rem', color: 'var(--text-color)',
              margin: '0 0 8px', lineHeight: 1.5,
            }}>
              {wf.description}
            </p>
          )}
          {chain.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3 }}>
              {chain.map((node, i) => {
                // dag_json from the API uses the raw shape: node.config.node_type / node.label
                // xyflow-transformed nodes use: node.data.config.node_type / node.data.label
                const type = node.config?.node_type
                  || node.data?.config?.node_type
                  || node.type
                  || 'input';
                const label = node.label
                  || node.data?.label
                  || node.config?.variable_name
                  || node.data?.config?.variable_name
                  || type.charAt(0).toUpperCase() + type.slice(1);
                const c = NODE_COLORS[type] || '#888';
                return (
                  <React.Fragment key={node.id}>
                    {i > 0 && (
                      <span style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', flexShrink: 0 }}>→</span>
                    )}
                    <span style={{
                      fontSize: '0.66rem', fontWeight: 600,
                      padding: '1px 6px', borderRadius: 99,
                      background: `${c}18`, color: c, border: `1px solid ${c}44`,
                      whiteSpace: 'nowrap',
                    }}>
                      {label}
                    </span>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// WorkflowDropdown — custom icon-aware dropdown for workflow selection
// ---------------------------------------------------------------------------

/**
 * Replaces the <select> with a button + popover so each option can display
 * its workflow icon alongside the name.
 *
 * The trigger is icon-only on every viewport (matching the Ask/Swarm mode
 * icons): the active workflow's icon, or a default workflow glyph when
 * nothing is selected, plus a chevron signalling the dropdown.  Hovering it
 * surfaces the same styled tooltip used by the other mode icons.
 */
function WorkflowDropdown({
  workflowList,
  activeWorkflowId,
  onWorkflowSelect,
  activeMode,
  onActivate,
  handleIconMouseEnter,
  handleIconMouseLeave,
  activeTooltip,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus search on open; clear on close
  useEffect(() => {
    if (open) {
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch('');
    }
  }, [open]);

  const activeWf = workflowList.find((w) => w.id === activeWorkflowId);
  const isWorkflowMode = activeMode === 'workflow';

  const q = search.trim().toLowerCase();
  const filteredList = q
    ? workflowList.filter((w) =>
        w.is_active !== false &&
        (w.name?.toLowerCase().includes(q) || w.description?.toLowerCase().includes(q))
      )
    : workflowList.filter((w) => w.is_active !== false);

  const handleSelect = useCallback((id) => {
    onWorkflowSelect && onWorkflowSelect(id || null);
    setOpen(false);
  }, [onWorkflowSelect]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Trigger — icon-only on every viewport, matching the Ask/Swarm mode
          icons (no text label, no pill).  Hover surfaces the styled mode
          tooltip; click opens the dropdown and switches to workflow mode. */}
      <span
        data-testid="mode-icon-workflow"
        aria-label="Workflows"
        onMouseEnter={() => handleIconMouseEnter && handleIconMouseEnter('workflow')}
        onMouseLeave={handleIconMouseLeave}
        onClick={() => { setOpen((o) => !o); onActivate && onActivate(); }}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          opacity: isWorkflowMode ? 1 : 0.5,
          color: isWorkflowMode ? 'var(--primary-color, #636cff)' : 'inherit',
          transition: 'all 0.2s ease',
        }}
      >
        <WorkflowIcon
          iconKey={activeWf?.icon || null}
          size={18}
          color={isWorkflowMode ? 'var(--primary-color, #636cff)' : 'inherit'}
        />
        {/* Chevron signals this is a dropdown without adding text */}
        <span style={{ fontSize: '1rem', opacity: 0.7, lineHeight: 1 }}>▾</span>
        {/* Styled mode tooltip — hidden once the dropdown is open so the two
            never overlap (both anchor above the trigger). */}
        {!open && activeTooltip === 'workflow' && (
          <div
            className="stack-tooltip"
            style={{ left: 0, bottom: '110%', top: 'auto', maxWidth: '280px' }}
            onClick={(e) => e.stopPropagation()}
          >
            {STACK_TOOLTIPS['workflow']}
          </div>
        )}
      </span>

      {/* Popover list — opens upward so it doesn't clip at the viewport bottom */}
      {open && (
        <div
          style={{
            position: 'absolute',
            bottom: '110%',
            top: 'auto',
            left: 0,
            zIndex: 1000,
            background: 'var(--panel-background, var(--input-background))',
            border: '1px solid var(--border-color, #ccc)',
            borderRadius: 6,
            boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
            minWidth: 300,
            width: 'max-content',
            maxWidth: 'min(420px, 90vw)',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 380,
          }}
        >
          {/* Sticky search header */}
          <div style={{
            padding: '8px 10px 6px',
            borderBottom: '1px solid var(--border-color, #eee)',
            flexShrink: 0,
          }}>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search workflows…"
              onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '5px 10px',
                borderRadius: 5,
                border: '1px solid var(--border-color, #ccc)',
                background: 'var(--input-background)',
                color: 'var(--text-color)',
                fontSize: '0.8rem',
                outline: 'none',
              }}
            />
          </div>

          {/* Scrollable list */}
          <div style={{ overflowY: 'auto', flex: 1, padding: '4px 0' }}>
            {/* "None / Ask mode" option — hidden when searching */}
            {!q && (
              <button
                type="button"
                onClick={() => handleSelect(null)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', padding: '6px 12px',
                  background: !activeWorkflowId ? 'var(--hover-color)' : 'transparent',
                  border: 'none', cursor: 'pointer',
                  fontSize: '0.82rem', color: 'var(--text-secondary)', textAlign: 'left',
                }}
              >
                <span>— None (Ask mode) —</span>
              </button>
            )}

            {/* Platform workflows */}
            {filteredList.some((w) => w.is_default) && (
              <div style={{
                padding: '4px 12px 2px', fontSize: '0.65rem', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.06em',
                color: 'var(--text-secondary)', opacity: 0.7,
              }}>
                Platform
              </div>
            )}
            {filteredList.filter((w) => w.is_default).map((wf) => (
              <WorkflowDropdownItem
                key={wf.id} wf={wf}
                isActive={wf.id === activeWorkflowId}
                onSelect={handleSelect}
              />
            ))}

            {/* User workflows */}
            {filteredList.some((w) => !w.is_default) && (
              <div style={{
                padding: '4px 12px 2px', fontSize: '0.65rem', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.06em',
                color: 'var(--text-secondary)', opacity: 0.7,
              }}>
                My Workflows
              </div>
            )}
            {filteredList.filter((w) => !w.is_default).map((wf) => (
              <WorkflowDropdownItem
                key={wf.id} wf={wf}
                isActive={wf.id === activeWorkflowId}
                onSelect={handleSelect}
              />
            ))}

            {/* Empty state when search matches nothing */}
            {filteredList.length === 0 && q && (
              <div style={{
                padding: '12px', fontSize: '0.82rem',
                color: 'var(--text-secondary)', textAlign: 'center',
              }}>
                No workflows match "{search}"
              </div>
            )}

            {/* Divider + Workflow Builder link */}
            <div style={{ height: 1, background: 'var(--border-color, #ccc)', margin: '4px 0' }} />
            <button
              type="button"
              onClick={() => navigate('/workflow-builder')}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '6px 12px',
                background: 'transparent', border: 'none',
                cursor: 'pointer', fontSize: '0.82rem',
                color: 'var(--text-secondary)', textAlign: 'left',
              }}
            >
              <WrenchIcon />
              <span>Workflow Builder</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ModeSelector
// ---------------------------------------------------------------------------

const ModeSelector = ({
  activeMode,
  setActiveMode,
  iconsToShow,
  handleIconMouseEnter,
  handleIconMouseLeave,
  handleIconClick,
  activeTooltip,
  // Workflow mode
  workflowList = [],
  activeWorkflowId = null,
  onWorkflowSelect,
  isMobile = false,
}) => {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', margin: isMobile ? '8px 0' : '12px 0', alignItems: 'center' }}>
      {STACK_ICONS['orchestrator'] && (
        <span
          data-testid="mode-icon-orchestrator"
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            opacity: activeMode === 'ask' ? 1 : 0.5,
            color: activeMode === 'ask' ? 'var(--primary-color, #636cff)' : 'inherit',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={() => handleIconMouseEnter('orchestrator')}
          onMouseLeave={handleIconMouseLeave}
          onClick={(e) => handleIconClick(e, 'orchestrator', 'ask')}
        >
          <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>{STACK_ICONS['orchestrator']}</span>
          {activeTooltip === 'orchestrator' && (
            <div className="stack-tooltip" style={{ left: 0, bottom: '110%', top: 'auto', maxWidth: '280px' }} onClick={(e) => e.stopPropagation()}>
              {STACK_TOOLTIPS['orchestrator']}
            </div>
          )}
        </span>
      )}

      <span
        data-testid="mode-icon-swarm"
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          opacity: activeMode === 'swarm' ? 1 : 0.5,
          color: activeMode === 'swarm' ? 'var(--primary-color, #636cff)' : 'inherit',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={() => handleIconMouseEnter('swarm')}
        onMouseLeave={handleIconMouseLeave}
        onClick={(e) => handleIconClick(e, 'swarm', 'swarm')}
      >
        <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>{STACK_ICONS['swarm']}</span>
        {activeTooltip === 'swarm' && (
          <div className="stack-tooltip" style={{ left: 0, bottom: '110%', top: 'auto', maxWidth: '280px' }} onClick={(e) => e.stopPropagation()}>
            {STACK_TOOLTIPS['swarm']}
          </div>
        )}
      </span>

      {/* Custom icon dropdown — appears after Swarm, before "|" */}
      {workflowList.length > 0 && (
        <WorkflowDropdown
          workflowList={workflowList}
          activeWorkflowId={activeWorkflowId}
          onWorkflowSelect={onWorkflowSelect}
          activeMode={activeMode}
          onActivate={setActiveMode ? () => setActiveMode('workflow') : undefined}
          handleIconMouseEnter={handleIconMouseEnter}
          handleIconMouseLeave={handleIconMouseLeave}
          activeTooltip={activeTooltip}
        />
      )}

      {iconsToShow.length > 1 && (
        <span style={{ color: 'var(--border-color, #ccc)', margin: '0 2px', fontSize: '1.2rem', fontWeight: '300' }}>|</span>
      )}

      {iconsToShow
        .filter((agentType) => agentType !== 'orchestrator')
        .map((agentType) => (
          <React.Fragment key={agentType}>
            {STACK_ICONS[agentType] && (
              <span
                style={{ position: 'relative', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onMouseEnter={() => handleIconMouseEnter(agentType)}
                onMouseLeave={handleIconMouseLeave}
                onClick={(e) => handleIconClick(e, agentType)}
              >
                <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>{STACK_ICONS[agentType]}</span>
                {activeTooltip === agentType && (
                  <div className="stack-tooltip" style={{ left: 0, bottom: '110%', top: 'auto', maxWidth: '280px' }} onClick={(e) => e.stopPropagation()}>
                    {STACK_TOOLTIPS[agentType]}
                  </div>
                )}
              </span>
            )}
          </React.Fragment>
        ))}
    </div>
  );
};

export default ModeSelector;
